<template>
  <div>
    <v-card class="rounded-card card-shadow mb-3">
      <v-card-text class="text--primary" >
        <v-row dense>
          <v-col cols="12" :sm="smMoeda">
            <div class="mb-3">
              {{ labelMoeda }}
            </div>
            <px-select-enum
              dense
              v-model="moeda"
              :items="moedasOptions"
              label="Moeda"
              class="required"
            />
          </v-col>
          <v-col cols="12" sm="6" v-if="this.permiteTaxaCambio()">
            <div class="mb-3">
              {{ labelTaxaCambio }}
            </div>
            <px-input-currency
                dense
                required
                :disabled="!this.isMoedaEstrangeira"
                label="Taxa de Câmbio"
                :class="`rounded-tag-small ${this.isMoedaEstrangeira ? 'required' : ''}`"
                :precision="4"
                :error="$v.resposta.taxaCambioFornecedor.$error"
                @blur="$v.resposta.taxaCambioFornecedor.$touch()"
                v-model="resposta.taxaCambioFornecedor"/>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <v-stepper class="elevation-1 rounded-card" v-model="currentStep" vertical>
      <v-stepper-step :complete="currentStep > 1" step="1"> Produtos</v-stepper-step>
      <v-stepper-content step="1">
        <px-resposta-cadastro-produtos
          :solicitacao="solicitacao"
          :resposta="resposta"
          :moeda="moeda"
          @next="next"
        />
      </v-stepper-content>

      <v-stepper-step :complete="currentStep > 2" step="2"> Condições</v-stepper-step>
      <v-stepper-content step="2">
        <px-resposta-cadastro-dados-basicos
          :resposta="resposta"
          @next="next"
          @previous="previous"
        />
      </v-stepper-content>

      <v-stepper-step :complete="currentStep > 3" step="3"> Frete</v-stepper-step>
      <v-stepper-content step="3">
        <px-resposta-cadastro-frete :resposta="resposta"
                                    :moeda="moeda"
                                    @next="next" @previous="previous" />
      </v-stepper-content>

      <v-stepper-step :complete="currentStep > 4" step="4">Confirmação</v-stepper-step>
      <v-stepper-content step="4">
        <px-resposta-cadastro-confirmacao v-if="currentStep === 4"
                                          :moeda="moeda"
                                          :resposta="resposta" />
        <v-btn
          color="primary"
          class="normal-btn-text rounded-tag-small"
          @click="enviarResposta"
          :loading="loading"
        >
          Enviar
        </v-btn>
        <v-btn text class="normal-btn-text" @click="previous" :disabled="loading"> Voltar</v-btn>
      </v-stepper-content>
    </v-stepper>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash';
import {
  SituacoesCotacao,
  SituacaoResposta,
  Moeda,
  SituacaoRespostaCotacaoProduto,
} from 'px-business-components';
import { mapGetters } from 'vuex';
import validationMixin from '@/mixins/validation-mixin';
import moedaMixin from '@/mixins/moeda-mixin';
import { requiredIf } from 'vuelidate/lib/validators';
import RespostaCadastroDadosBasicos from './RespostaCadastroDadosBasicos.vue';
import RespostaCadastroFrete from './RespostaCadastroFrete.vue';
import RespostaCadastroProdutos from './RespostaCadastroProdutos.vue';
import RespostaCadastroConfirmacao from './RespostaCadastroConfirmacao.vue';
import RespostaService from './resposta-service';
import { defaultValue } from './resposta-utils';

export default {
  mixins: [validationMixin, moedaMixin],
  props: {
    solicitacao: Object,
    moedas: Array,
  },
  data() {
    return {
      loading: false,
      currentStep: 1,
      moeda: Moeda.keys.BRL.key,
      resposta: {
        fornecedor: this.solicitacao.fornecedor,
        cotacao: this.solicitacao.cotacao,
        situacao: SituacaoResposta.keys.RESPONDIDA.key,
        formaPagamento: null,
        prazoPagamento: null,
        validade: null,
        observacao: null,
        frete: {
          tipoFrete: null,
          totalTransporte: 0,
          observacaoFrete: null,
          prazoEntrega: null,
          valorMinimoFreteGratis: null,
          ofertaFreteGratis: null,
        },
        produtos: null,
        taxaCambioFornecedor: null,
      },
    };
  },
  computed: {
    ...mapGetters({
      cotacaoId: 'cotacaoId',
    }),
    moedasOptions() {
      const moedasDisponiveis = this.moedas.map(m => m.moeda);
      return Moeda.values.filter(moeda => moedasDisponiveis.includes(moeda.key));
    },
    isEdicao() {
      return !!this.solicitacao.respostaCotacao?.id;
    },
    smMoeda() {
      return this.solicitacao.detalhesFornecedor?.permiteTaxaCambio ? 6 : 12;
    },
    labelMoeda() {
      return this.solicitacao.detalhesFornecedor?.permiteTaxaCambio
        ? 'Selecione a moeda que deseja utilizar para responder a cotação.'
        : 'Selecione a moeda que deseja utilizar para responder a cotação. Faremos a conversão dos valores conforme a taxa PTAX atual e atualizaremos o valor diáriamente.';
    },
    labelTaxaCambio() {
      return 'Informe a taxa de câmbio que deseja utilizar para responder a cotação.';
    },
  },
  methods: {
    next() {
      this.currentStep = this.currentStep + 1;
    },
    previous() {
      this.currentStep = this.currentStep - 1;
    },
    enviarResposta() {
      if (this.validateForm()) {
        return;
      }
      this.loading = true;
      if (this.isEdicao) {
        RespostaService.update(this.cotacaoId, this.buildResposta())
          .then(() => {
            this.$toast('Resposta enviada com sucesso', { color: 'success' });
            this.$router.go(-1);
          })
          .finally(() => {
            this.loading = false;
          });
      } else {
        RespostaService.save(this.cotacaoId, this.buildResposta())
          .then(() => {
            this.$toast('Resposta enviada com sucesso', { color: 'success' });
            this.$router.go(-1);
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },
    buildResposta() {
      const resposta = cloneDeep(this.resposta);
      resposta.moeda = this.moeda;
      resposta.taxaCambioFornecedor = this.permiteTaxaCambio()
        ? this.resposta.taxaCambioFornecedor
        : null;
      resposta.produtos = resposta.produtos.map((produto) => {
        if (produto.situacao === SituacaoRespostaCotacaoProduto.keys.RESPONDIDA.key) {
          return produto;
        }
        return {
          id: produto.id,
          ...defaultValue(produto.situacao, produto.cotacaoProduto),
        };
      });
      return resposta;
    },
    criarRespostaProduto(produto) {
      return {
        cotacaoProduto: produto,
        situacao: SituacaoResposta.keys.RESPONDIDA.key,
        precoUnitario: null,
        especificacaoProduto: null,
        impostos: {
          tipoAplicacaoIcms: null,
          tipoAplicacaoIpi: null,
          icms: null,
          ipi: null,
          valorIcms: 0,
          valorIpi: 0,
          total: 0,
          totalProdutos: 0,
          percentualRateio: 0,
          frete: 0,
        },
        arquivos: [],
      };
    },
    retornarProdutosFaltantes() {
      return this.solicitacao.cotacao.produtos
        .filter(produto => SituacoesCotacao.ABERTAS.includes(produto.situacao)
          && !this.solicitacao.respostaCotacao.produtos
            .some(respostaProduto => respostaProduto.cotacaoProduto.id === produto.id))
        .map(produto => this.criarRespostaProduto(produto));
    },
    permiteTaxaCambio() {
      return this.solicitacao.detalhesFornecedor?.permiteTaxaCambio;
    },
  },
  watch: {
    moeda() {
      if (!this.isMoedaEstrangeira) {
        this.resposta.taxaCambioFornecedor = null;
      }
    },
  },
  created() {
    if (this.isEdicao) {
      this.resposta = this.solicitacao.respostaCotacao;
      this.resposta.produtos.push(...this.retornarProdutosFaltantes());
      this.moeda = this.resposta.moeda;
    } else {
      this.resposta.produtos = this.solicitacao.cotacao.produtos
        .filter(produto => SituacoesCotacao.ABERTAS.includes(produto.situacao))
        .map(produto => this.criarRespostaProduto(produto));
    }
  },
  validations() {
    return {
      resposta: {
        taxaCambioFornecedor: {
          required: requiredIf(function () {
            return this.permiteTaxaCambio() && this.isMoedaEstrangeira;
          }),
          minValue: value => !this.permiteTaxaCambio() || !this.isMoedaEstrangeira || value > 0,
        },
      },
    };
  },
  components: {
    pxRespostaCadastroDadosBasicos: RespostaCadastroDadosBasicos,
    pxRespostaCadastroFrete: RespostaCadastroFrete,
    pxRespostaCadastroProdutos: RespostaCadastroProdutos,
    pxRespostaCadastroConfirmacao: RespostaCadastroConfirmacao,
  },
};
</script>
